import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import SeoMeta from "@/components/SeoMeta";
import { buildOrganizationSchema, buildWebSiteSchema, buildWebPageSchema, PRODUCTION_ORIGIN } from "@/lib/seo";
import { listCalculators } from "@/lib/calculators/registry";
import { Display, Body, Caption } from "@/components/ui/typography";
import { Card } from "@/components/ui/card";
import CalculatorIcon from "@/components/calculator/CalculatorIcon";
import HeroCalculatorPreview from "@/components/calculator/HeroCalculatorPreview";

export default function Home() {
  const calculators = useMemo(() => listCalculators(), []);

  const title = "Financial Calculators | Salary, Mortgage & Loans | Fyunor";
  const description =
    "Free, accurate financial calculators for salary, mortgage, loan, and compound interest. See your numbers instantly — no sign-up, no clutter, just clear answers.";
  const seo = {
    title,
    description,
    canonical: "/",
    robots: "index, follow",
    structuredData: [
      buildOrganizationSchema(),
      buildWebSiteSchema(),
      buildWebPageSchema({ title, description, url: `${PRODUCTION_ORIGIN}/` }),
    ],
  };

  return (
    <>
      <SeoMeta {...seo} />
      <AppShell>
        {/* Hero */}
        <section className="container-fyunor py-6 lg:py-8">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
            <div className="max-w-2xl">
              <Caption className="text-primary">Financial Calculators</Caption>
              <Display className="mt-2 text-foreground">Clear, accurate answers in seconds.</Display>
              <Body className="mt-4 text-lg text-muted-foreground">
                A focused library of financial calculators — fast, trustworthy, and easy to understand.
                No sign-up, no clutter, just the numbers you need.
              </Body>
            </div>
            <HeroCalculatorPreview />
          </div>
        </section>

        {/* Calculators */}
        {calculators.length > 0 && (
          <section className="container-fyunor pb-16 lg:pb-24">
            <Caption>Available now</Caption>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {calculators.map((c) => (
                <Link key={c.id} to={c.countries?.[0] && c.countries[0] !== "*" ? `/${c.countries[0].toLowerCase()}/calculators/${c.slug}` : `/calculators/${c.slug}`}>
                  <Card interactive className="h-full p-5">
                    <Caption>{c.category}</Caption>
                    <div className="mt-2 flex items-center gap-3">
                      <CalculatorIcon id={c.id} size={24} />
                      <div className="font-heading text-lg font-semibold text-foreground">{c.name}</div>
                    </div>
                    <Body className="mt-1 text-sm text-muted-foreground line-clamp-2">{c.description}</Body>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </AppShell>
    </>
  );
}